import { STAGES } from "src/utils/app-constants";
import AWS from "aws-sdk";
import {UPDATE} from "src/constants/program-source";

interface S3PathDetail {
    bucket: string;
    key: string;
}

export const bulkStartWorkflowInstanceS3Beta: S3PathDetail = {
    bucket: "bulk-start-inputs-beta",
    key: "bulkRequests/",
};

export const bulkStartWorkflowInstanceS3Gamma: S3PathDetail = {
    bucket: "bulk-start-inputs-gamma",
    key: "bulkRequests/",
};

export const bulkStartWorkflowInstanceS3Prod: S3PathDetail = {
    bucket: "bulk-start-inputs-prod",
    key: "bulkRequests/",
};

// Get Bulk Start workflow instance s3 details
export function bulkStartWorkflowInstanceS3Details(stage: string): S3PathDetail {
    let bulkStartWorkflowInstanceS3Config;

    switch (stage) {
        case STAGES.PROD.toLowerCase():
            bulkStartWorkflowInstanceS3Config = bulkStartWorkflowInstanceS3Prod;
            break;
        case STAGES.GAMMA.toLowerCase():
            bulkStartWorkflowInstanceS3Config = bulkStartWorkflowInstanceS3Gamma;
            break;
        default:
            bulkStartWorkflowInstanceS3Config = bulkStartWorkflowInstanceS3Beta;
    }
    return bulkStartWorkflowInstanceS3Config;
}

/**
 * Type for bulk workflow operation types
 */
export type BulkOperationType = 'restart' | 'redrive';

/**
 * S3 bucket configuration for bulk workflow operations
 */
const bulkOperationS3Config = {
    beta: {
        bucket: "swo-bulk-action-beta",
    },
    prod: {
        bucket: "swo-bulk-action-prod",
    }
};

/**
 * S3 key prefixes for different bulk operation types
 */
const bulkOperationKeyPrefixes: Record<BulkOperationType, string> = {
    restart: "bulkRestarts/",
    redrive: "bulkRedrives/",
};

/**
 * Unified function to get S3 details for bulk workflow operations (restart/redrive)
 * @param stage - Environment stage (alpha, beta, gamma, prod)
 * @param operationType - Type of bulk operation ('restart' | 'redrive')
 * @returns S3PathDetail with bucket and key
 */
function getBulkOperationS3Details(stage: string, operationType: BulkOperationType): S3PathDetail {
    const isProd = stage === STAGES.PROD.toLowerCase() || stage === STAGES.GAMMA.toLowerCase();
    const bucketConfig = isProd ? bulkOperationS3Config.prod : bulkOperationS3Config.beta;
    
    return {
        bucket: bucketConfig.bucket,
        key: bulkOperationKeyPrefixes[operationType],
    };
}

// Legacy exports for backward compatibility
export const bulkRestartWorkflowInstanceS3Beta: S3PathDetail = {
    bucket: "swo-bulk-action-beta",
    key: "bulkRestarts/",
};

export const bulkRestartWorkflowInstanceS3Prod: S3PathDetail = {
    bucket: "swo-bulk-action-prod",
    key: "bulkRestarts/",
};

/**
 * Get S3 details for bulk restart workflow operation
 * @deprecated Use getBulkOperationS3Details internally. This function is kept for backward compatibility.
 */
export function bulkRestartWorkflowInstanceS3Details(stage: string): S3PathDetail {
    return getBulkOperationS3Details(stage, 'restart');
}

export const bulkRedriveWorkflowInstanceS3Beta: S3PathDetail = {
    bucket: "swo-bulk-action-beta",
    key: "bulkRedrives/",
};

export const bulkRedriveWorkflowInstanceS3Prod: S3PathDetail = {
    bucket: "swo-bulk-action-prod",
    key: "bulkRedrives/",
};

/**
 * Get S3 details for bulk redrive workflow operation
 * @deprecated Use getBulkOperationS3Details internally. This function is kept for backward compatibility.
 */
export function bulkRedriveWorkflowInstanceS3Details(stage: string): S3PathDetail {
    return getBulkOperationS3Details(stage, 'redrive');
}


const ProgramSourceS3Config = (stage: string, operation: string): S3PathDetail => ({
    bucket: `program-source-ui-helper-${stage.toLowerCase()}`,
    key: `program-source-${operation}-action/`,
});

export function programSourceS3Details(stage: string, operation: typeof UPDATE): S3PathDetail {
    const stageConfigs = {
        [STAGES.ALPHA.toLowerCase()]: {
            update: ProgramSourceS3Config(STAGES.ALPHA, UPDATE),
        },
        [STAGES.BETA.toLowerCase()]: {
            update: ProgramSourceS3Config(STAGES.BETA, UPDATE),
        },
        [STAGES.GAMMA.toLowerCase()]: {
            update: ProgramSourceS3Config(STAGES.GAMMA, UPDATE),
        },
        [STAGES.PROD.toLowerCase()]: {
            update: ProgramSourceS3Config(STAGES.PROD, UPDATE),
        },
    };

    return stageConfigs[stage.toLowerCase()]?.[operation] || stageConfigs[STAGES.BETA.toLowerCase()][operation];
}

export const getDownloadUrl = (bucket: string, filePath: string, fileNameMatch?: string): Promise<string> => {
    // TODO: Remove this region hardcoding once we start to get bucket region as metadata
    const s3Service = new AWS.S3({ region: "us-east-1" });
    return new Promise((resolve, reject) => {
        s3Service.listObjectsV2({ Prefix: filePath, Bucket: bucket }, (err, data) => {
            if (err) {
                reject(err.message);
            }
            const dataKey =
                fileNameMatch && data.Contents
                    ? data.Contents.find((listItem) => listItem.Key?.includes(fileNameMatch))
                    : data.Contents?.[0];
            dataKey &&
            s3Service.getSignedUrl(
                "getObject",
                {
                    Bucket: bucket,
                    Key: dataKey.Key,
                    Expires: 600,
                },
                (error, url) => {
                    if (error) {
                        reject(error.message);
                    }
                    resolve(url);
                }
            );
        });
    });
};
